import { Link, Paragraph } from '@vimeo/iris/typography';

import CodeBlock from 'src/_components/CodeBlock';

export default function getPlayerReferenceDoc() {
  return {
    'Methods for playback controls': {
      type: 'method',
      items: [
        {
          name: 'Get the autopause state of a player or browser',
          description: (
            <Paragraph size="2">
              This method gets the autopause state of a player or browser, where <code>true</code>{' '}
              indicates that autopause is enabled. Under autopause, whenever a new video loads in
              the browser window, the video begins in a paused state.
            </Paragraph>
          ),
          note: "The autopause feature has no effect if you've disabled cookies in your browser, either through browser settings or with an extension or plugin.",
          function: {
            name: 'getAutopause()',
            returns: 'Promise <boolean, (UnsupportedError | Error)>',
            errors: [
              {
                error: 'UnsupportedError',
                description: "Autopause isn't supported by the current player or browser.",
              },
              {
                error: 'Error',
                description: 'Some other error occurred.',
              },
            ],
          },
          example: (
            <CodeBlock language="javascript">{`player.getAutopause().then(function(autopause) {
  // \`autopause\` indicates whether autopause is enabled
}).catch(function(error) {
  switch (error.name) {
    case 'UnsupportedError':
      // Autopause isn't supported by the current player or browser
      break;
    default:
      // Some other error occurred
      break;
  }
});`}</CodeBlock>
          ),
        },
        {
          name: 'Get the playback position of a video',
          description: (
            <Paragraph size="2">
              This method gets the current playback position of a video, measured in seconds.
            </Paragraph>
          ),
          function: {
            name: 'getCurrentTime()',
            returns: 'Promise <number>',
          },
          example: (
            <CodeBlock language="javascript">{`player.getCurrentTime().then(function(seconds) {
  // \`seconds\` indicates the current playback position of the video
});`}</CodeBlock>
          ),
        },
        {
          name: 'Get the duration of a video',
          description: (
            <Paragraph size="2">
              This method gets the duration of the video in seconds, rounded to the nearest second
              before playback begins and to the nearest thousandth of a second after playback
              begins.
            </Paragraph>
          ),
          function: {
            name: 'getDuration()',
            returns: 'Promise <number>',
          },
          example: (
            <CodeBlock language="javascript">{`player.getDuration().then(function(duration) {
  // \`duration\` indicates the duration of the video in seconds
});`}</CodeBlock>
          ),
        },
        {
          name: 'Get the ended state of a video',
          description: (
            <Paragraph size="2">
              This method gets the ended state of the video, where <code>true</code> indicates that
              the video has ended. The video has ended if its current playback position is exactly
              equal to its duration.
            </Paragraph>
          ),
          function: {
            name: 'getEnded()',
            returns: 'Promise <boolean>',
          },
          example: (
            <CodeBlock language="javascript">{`player.getEnded().then(function(ended) {
  // \`ended\` indicates whether the video has ended
});`}</CodeBlock>
          ),
        },
        {
          name: 'Get the loop state of a player',
          description: (
            <Paragraph size="2">
              This method gets the loop state of a player, where <code>true</code> indicates that
              the video restarts once it reaches the end of playback.
            </Paragraph>
          ),
          function: {
            name: 'getLoop()',
            returns: 'Promise <boolean>',
          },
          example: (
            <CodeBlock language="javascript">{`player.getLoop().then(function(loop) {
  // \`loop\` indicates whether the loop behavior is active
});`}</CodeBlock>
          ),
        },
        {
          name: 'Get the pause state of a player',
          description: (
            <Paragraph size="2">
              This method gets the pause state of the current player, where <code>true</code>{' '}
              indicates that playback is paused.
            </Paragraph>
          ),
          function: {
            name: 'getPaused()',
            returns: 'Promise <boolean>',
          },
          example: (
            <CodeBlock language="javascript">{`player.getPaused().then(function(paused) {
  // \`paused\` indicates whether the player is paused
});`}</CodeBlock>
          ),
        },
        {
          name: 'Get the playback rate of a player',
          description: (
            <Paragraph size="2">
              This method gets the playback rate of a player on a scale from 0.5 to 2, where 0.5 is
              half speed and 2 is double speed.
            </Paragraph>
          ),
          note: 'Adjusting the playback rate is available only to members with a Vimeo Pro subscription or higher.',
          function: {
            name: 'getPlaybackRate()',
            returns: 'Promise <number>',
          },
          example: (
            <CodeBlock language="javascript">{`player.getPlaybackRate().then(function(playbackRate) {
  // \`playbackRate\` indicates the numeric value of the current playback rate
});`}</CodeBlock>
          ),
        },
        {
          name: 'Get the volume level of a player',
          description: (
            <Paragraph size="2">
              This method gets the volume level of a player on a scale of 0 to 1.
            </Paragraph>
          ),
          note: (
            <span>
              Most mobile devices don't support a volume level independent of the system volume. In
              these cases, this method always returns <code>1</code>.
            </span>
          ),
          function: {
            name: 'getVolume()',
            returns: 'Promise <number>',
          },
          example: (
            <CodeBlock language="javascript">{`player.getVolume().then(function(volume) {
  // \`volume\` indicates the volume level of the player
});`}</CodeBlock>
          ),
        },
        {
          name: 'Pause a video',
          description: <Paragraph size="2">This method pauses the playback of a video.</Paragraph>,
          function: {
            name: 'pause()',
            returns: 'Promise <void, (PasswordError | PrivacyError | Error)>',
            errors: [
              {
                error: 'PasswordError',
                description: 'The video is password-protected.',
              },
              {
                error: 'PrivacyError',
                description: 'The video is private.',
              },
              {
                error: 'Error',
                description: 'Some other error occurred.',
              },
            ],
          },
          example: (
            <CodeBlock language="javascript">{`player.pause().then(function() {
  // The video is paused
}).catch(function(error) {
  switch (error.name) {
    case 'PasswordError':
        // The video is password-protected
        break;
    case 'PrivacyError':
        // The video is private
        break;
    default:
        // Some other error occurred
        break;
  }
});`}</CodeBlock>
          ),
        },
        {
          name: 'Play a video',
          description: <Paragraph size="2">This method plays a video.</Paragraph>,
          note: (
            <span>
              On certain mobile devices, including those running iOS, and in certain desktop
              browsers like Chrome and Safari, you can't programmatically trigger the initial
              playback of a video. This method becomes available once the viewer has started
              playback by manually tapping the video's play button. For more information on working
              with autoplay in various browsers, see our{' '}
              <Link
                variant="minimal"
                href="https://vimeo.com/help/sso?redirect_to=https://help.vimeo.com/hc/en-us/articles/12426486963857-Autoplay-and-loop-embedded-videos"
              >
                Help Center
              </Link>
              .
            </span>
          ),
          function: {
            name: 'play()',
            returns: 'Promise <void, (PasswordError | PrivacyError | Error)>',
            errors: [
              {
                error: 'PasswordError',
                description: 'The video is password-protected.',
              },
              {
                error: 'PrivacyError',
                description: 'The video is private.',
              },
              {
                error: 'Error',
                description: 'Some other error occurred.',
              },
            ],
          },
          example: (
            <CodeBlock language="javascript">{`player.play().then(function() {
  // The video is playing
}).catch(function(error) {
  switch (error.name) {
    case 'PasswordError':
        // The video is password-protected
        break;
    case 'PrivacyError':
        // The video is private
        break;
    default:
        // Some other error occurred
        break;
  }
});`}</CodeBlock>
          ),
        },
        {
          name: 'Set the autopause state of a player or browser',
          description: (
            <div>
              <Paragraph size="2">
                This method enables or disables autopause in a player or browser, where{' '}
                <code>true</code> indicates that autopause is enabled. Under autopause, whenever a
                new video loads in the browser window, the video begins in a paused state.
              </Paragraph>
              <Paragraph size="2">
                We recommend that you leave autopause enabled unless you have a specific reason for
                disabling it.
              </Paragraph>
            </div>
          ),
          note: "The autopause feature has no effect if you've disabled cookies in your browser, either through browser settings or with an extension or plugin.",
          function: {
            name: 'setAutopause({autopause})',
            parameters: [
              {
                parameter: (
                  <code>
                    <b>autopause</b>
                  </code>
                ),
                type: 'boolean',
                required: true,
                description: 'The autopause state to set.',
              },
            ],
            returns: 'Promise <boolean, (UnsupportedError | Error)>',
            errors: [
              {
                error: 'UnsupportedError',
                description: "Autopause isn't supported by the current player or browser.",
              },
              {
                error: 'Error',
                description: 'Some other error occurred.',
              },
            ],
          },
          example: (
            <CodeBlock language="javascript">{`player.setAutopause(false).then(function(autopause) {
  // Autopause is disabled
}).catch(function(error) {
  switch (error.name) {
    case 'UnsupportedError':
        // Autopause isn't supported by the current player or browser
        break;
    default:
        // Some other error occurred
        break;
  }
});`}</CodeBlock>
          ),
        },
        {
          name: 'Set the playback position of a video',
          description: (
            <div>
              <Paragraph size="2">
                This method sets the current playback position in seconds. The player attempts to
                seek to as close to the specified time as possible. The exact time comes back as the
                fulfilled value of the promise.
              </Paragraph>

              <ul>
                <li>
                  <strong>If playback hasn't started yet,</strong> using this method starts
                  playback.
                </li>
                <li>
                  <strong>If playback has already started,</strong> using this method doesn't affect
                  the play state. If the player is paused, it remains paused. If the player is
                  playing, it buffers the video from the new position and then resumes playing.
                </li>
              </ul>
            </div>
          ),
          note: (
            <span>
              On certain mobile devices, including those running iOS, and in certain desktop
              browsers like Chrome and Safari, you can't programmatically trigger the initial
              playback of a video. This method becomes available once the viewer has started
              playback by manually tapping the video's play button. For more information on working
              with autoplay in various browsers, see our{' '}
              <Link
                variant="minimal"
                href="https://vimeo.com/help/sso?redirect_to=https://help.vimeo.com/hc/en-us/articles/12426486963857-Autoplay-and-loop-embedded-videos"
              >
                Help Center
              </Link>
              .
            </span>
          ),
          function: {
            name: 'setCurrentTime(seconds)',
            parameters: [
              {
                parameter: (
                  <code>
                    <b>seconds</b>
                  </code>
                ),
                type: 'number',
                required: true,
                description: 'The playback position in seconds.',
              },
            ],
            returns: 'Promise <number, (RangeError | Error)>',
            errors: [
              {
                error: 'RangeError',
                description: "The time is less than 0 or greater than the video's duration.",
              },
              {
                error: 'Error',
                description: 'Some other error occurred.',
              },
            ],
          },
          example: (
            <CodeBlock language="javascript">{`player.setCurrentTime(30.456).then(function(seconds) {
  // \`seconds\` indicates the actual time that the player seeks to
}).catch(function(error) {
  switch (error.name) {
    case 'RangeError':
        // The time is less than 0 or greater than the video's duration
        break;
    default:
        // Some other error occurred
        break;
  }
});`}</CodeBlock>
          ),
        },
        {
          name: 'Set the loop state of a player',
          description: (
            <Paragraph size="2">
              This method sets the loop state of the player. When the loop state is{' '}
              <code>true</code>, playback resumes at the beginning of the video immediately after
              the video ends.
            </Paragraph>
          ),
          note: (
            <span>
              When the loop state is active, the <code>ended</code> event never fires.
            </span>
          ),
          function: {
            name: 'setLoop(loop)',
            parameters: [
              {
                parameter: (
                  <code>
                    <b>loop</b>
                  </code>
                ),
                type: 'boolean',
                required: true,
                description: 'Whether the player loops video playback.',
              },
            ],
            returns: 'Promise <boolean>',
          },
          example: (
            <CodeBlock language="javascript">{`player.setLoop(true).then(function(loop) {
  // The loop behavior is enabled
});`}</CodeBlock>
          ),
        },
        {
          name: 'Set the playback rate of a player',
          description: (
            <Paragraph size="2">
              This method sets the playback rate of the player on a scale from 0.5 to 2, where 0.5
              is half speed and 2 is double speed. When you set the playback rate through the API,
              the specified value isn't synchronized to other players or stored as the viewer's
              preference.
            </Paragraph>
          ),
          note: 'Adjusting the playback rate is available only to members with a Vimeo Pro subscription or higher.',
          function: {
            name: 'setPlaybackRate(playbackRate)',
            parameters: [
              {
                parameter: (
                  <code>
                    <b>playbackRate</b>
                  </code>
                ),
                type: 'number',
                required: true,
                description: 'The playback rate of the player from 0.5 to 2.',
              },
            ],
            returns: 'Promise <number, (RangeError | Error)>',
            errors: [
              {
                error: 'RangeError',
                description: 'The playback rate is less than 0.5 or greater than 2.',
              },
              {
                error: 'Error',
                description: 'Some other error occurred.',
              },
            ],
          },
          example: (
            <CodeBlock language="javascript">{`player.setPlaybackRate(0.5).then(function(playbackRate) {
  // The playback rate is set
}).catch(function(error) {
  switch (error.name) {
    case 'RangeError':
        // The playback rate is less than 0.5 or greater than 2
        break;
    default:
        // Some other error occurred
        break;
  }
});`}</CodeBlock>
          ),
        },
        {
          name: 'Set the volume level of a player',
          description: (
            <Paragraph size="2">
              This method sets the volume level of the player on a scale from 0 to 1. When you set
              the volume through the API, the specified value isn't synchronized to other players or
              stored as the viewer's preference.
            </Paragraph>
          ),
          note: "Most mobile devices don't enable you to set the volume independently of the system. Even so, the method doesn't return an error in this situation.",
          function: {
            name: 'setVolume(volume)',
            parameters: [
              {
                parameter: (
                  <code>
                    <b>volume</b>
                  </code>
                ),
                type: 'number',
                required: true,
                description: 'The volume level of the player from 0 to 1.',
              },
            ],
            returns: 'Promise <number, (RangeError | Error)>',
            errors: [
              {
                error: 'RangeError',
                description: 'The volume is less than 0 or greater than 1.',
              },
              {
                error: 'Error',
                description: 'Some other error occurred.',
              },
            ],
          },
          example: (
            <CodeBlock language="javascript">{`player.setVolume(0.5).then(function(volume) {
  // The volume is set
}).catch(function(error) {
  switch (error.name) {
    case 'RangeError':
        // The volume is less than 0 or greater than 1
        break;
    default:
        // Some other error occurred
        break;
  }
});`}</CodeBlock>
          ),
        },
      ],
    },
    'Events for playback controls': {
      type: 'event',
      items: [
        {
          name: 'bufferend',
          description: (
            <div>
              <Paragraph size="2">This event fires under three conditions:</Paragraph>

              <ul>
                <li>When buffering ends in the player</li>
                <li>At the end of preload</li>
                <li>At the end of seeking</li>
              </ul>
            </div>
          ),
          example: (
            <CodeBlock language="javascript">{`var onPlay = function(data) {
  // Function logic goes here
};
player.on('bufferend', onPlay);`}</CodeBlock>
          ),
        },
        {
          name: 'bufferstart',
          description: (
            <div>
              <Paragraph size="2">This event fires under three conditions:</Paragraph>

              <ul>
                <li>When buffering starts in the player</li>
                <li>During preload</li>
                <li>During seeking</li>
              </ul>
            </div>
          ),
          example: (
            <CodeBlock language="javascript">{`var onPlay = function(data) {
  // Function logic goes here
};
player.on('bufferstart', onPlay);`}</CodeBlock>
          ),
        },
        {
          name: 'ended',
          description: (
            <Paragraph size="2">
              This event fires when playback reaches the end of a video.
            </Paragraph>
          ),
          note: (
            <span>
              When the player's loop behavior is enabled, the <code>ended</code> event doesn't fire.
            </span>
          ),
          event: {
            data: [
              {
                property: (
                  <code>
                    <b>duration</b>
                  </code>
                ),
                description: 'The length of the video in seconds.',
              },
              {
                property: (
                  <code>
                    <b>percent</b>
                  </code>
                ),
                description:
                  'The amount of the video that has played in comparison to the length of the video; multiply by 100 to obtain the percentage.',
              },
              {
                property: (
                  <code>
                    <b>seconds</b>
                  </code>
                ),
                description: 'The amount of the video, in seconds, that has played.',
              },
            ],
            sample: {
              duration: 61.857,
              percent: 1,
              seconds: 61.857,
            },
          },
          example: (
            <CodeBlock language="javascript">{`var onPlay = function(data) {
  console.log(data.duration);
  console.log(data.percent);
  console.log(data.seconds);
};
player.on('ended', onPlay);`}</CodeBlock>
          ),
        },
        {
          name: 'error',
          description: (
            <Paragraph size="2">
              This event fires when the player experiences some sort of error. If a method call
              generated the error, the name of the method appears in the event data, along with the
              name of the error.
            </Paragraph>
          ),
          note: (
            <span>
              We recommend that you use <code>.catch()</code> on each method call instead of
              globally listening for error events.
            </span>
          ),
          event: {
            data: [
              {
                property: (
                  <code>
                    <b>message</b>
                  </code>
                ),
                description: 'The error message.',
              },
              {
                property: (
                  <code>
                    <b>method</b>
                  </code>
                ),
                description: 'The method that generated the error.',
              },
              {
                property: (
                  <code>
                    <b>name</b>
                  </code>
                ),
                description: 'The name of the error.',
              },
            ],
            sample: {
              message:
                '#984220 does not meet the minimum contrast ratio. We recommend using brighter colors. (You could try #D35E30 instead.) See WCAG 2.0 guidelines: http://www.w3.org/TR/WCAG/#visual-audio-contrast',
              method: 'setColor',
              name: 'ContrastError',
            },
          },
        },
        {
          name: 'loaded',
          description: (
            <Paragraph size="2">
              This event fires when a new video is loaded in the player.
            </Paragraph>
          ),
          event: {
            data: [
              {
                property: (
                  <code>
                    <b>id</b>
                  </code>
                ),
                description: 'The ID of the new video.',
              },
            ],
            sample: { id: 76979871 },
          },
        },
        {
          name: 'pause',
          description: <Paragraph size="2">This event fires when the video is paused.</Paragraph>,
          event: {
            data: [
              {
                property: (
                  <code>
                    <b>duration</b>
                  </code>
                ),
                description: 'The length of the video in seconds.',
              },
              {
                property: (
                  <code>
                    <b>percent</b>
                  </code>
                ),
                description:
                  'The amount of the video that has played to the pause position in comparison to the length of the video; multiply by 100 to obtain the percentage.',
              },
              {
                property: (
                  <code>
                    <b>seconds</b>
                  </code>
                ),
                description:
                  'The amount of the video, in seconds, that has played to the pause position.',
              },
            ],
            sample: {
              duration: 61.857,
              percent: 0,
              seconds: 0,
            },
          },
        },
        {
          name: 'play',
          description: <Paragraph size="2">This event fires when the video plays.</Paragraph>,
          event: {
            data: [
              {
                property: (
                  <code>
                    <b>duration</b>
                  </code>
                ),
                description: 'The length of the video in seconds.',
              },
              {
                property: (
                  <code>
                    <b>percent</b>
                  </code>
                ),
                description:
                  'The amount of the video that has played in comparison to the length of the video; multiply by 100 to obtain the percentage.',
              },
              {
                property: (
                  <code>
                    <b>seconds</b>
                  </code>
                ),
                description: 'The amount of the video, in seconds, that has played.',
              },
            ],
            sample: {
              duration: 61.857,
              percent: 0,
              seconds: 0,
            },
          },
        },
        {
          name: 'playbackratechange',
          description: (
            <Paragraph size="2">
              This event fires when the playback rate of the video in the player changes.
            </Paragraph>
          ),
          note: "If the creator of the video has disabled the ability of the viewer to change the playback rate, this event doesn't fire.",
          event: {
            data: [
              {
                property: (
                  <code>
                    <b>playbackRate</b>
                  </code>
                ),
                description: 'The new playback rate.',
              },
            ],
            sample: { playbackRate: 1.5 },
          },
        },
        {
          name: 'progress',
          description: (
            <Paragraph size="2">
              This event fires while the video is loading. The event data indicates the amount of
              the video that has been buffered.
            </Paragraph>
          ),
          event: {
            data: [
              {
                property: (
                  <code>
                    <b>duration</b>
                  </code>
                ),
                description: 'The length of the video in seconds.',
              },
              {
                property: (
                  <code>
                    <b>percent</b>
                  </code>
                ),
                description:
                  'The amount of the video that has buffered in comparison to the length of the video; multiply by 100 to obtain the percentage.',
              },
              {
                property: (
                  <code>
                    <b>seconds</b>
                  </code>
                ),
                description: 'The amount of the video, in seconds, that has buffered.',
              },
            ],
            sample: {
              duration: 61.857,
              percent: 0.502,
              seconds: 31.052,
            },
          },
        },
        {
          name: 'seeked',
          description: (
            <Paragraph size="2">
              This event fires when the player seeks to a specific time. A simultaneous{' '}
              <code>timeupdate</code> event also fires.
            </Paragraph>
          ),
          event: {
            data: [
              {
                property: (
                  <code>
                    <b>duration</b>
                  </code>
                ),
                description: 'The length of the video in seconds.',
              },
              {
                property: (
                  <code>
                    <b>percent</b>
                  </code>
                ),
                description:
                  'The amount of the video that has played from the new seek position in comparison to the length of the video; multiply by 100 to obtain the percentage.',
              },
              {
                property: (
                  <code>
                    <b>seconds</b>
                  </code>
                ),
                description:
                  'The amount of the video, in seconds, that has played from the new seek position.',
              },
            ],
            sample: {
              duration: 61.857,
              percent: 0.485,
              seconds: 30,
            },
          },
        },
        {
          name: 'timeupdate',
          description: (
            <Paragraph size="2">
              This event fires when the playback position of the video changes, generally every 250
              ms during playback, but the interval can vary depending on the browser.
            </Paragraph>
          ),
          event: {
            data: [
              {
                property: (
                  <code>
                    <b>duration</b>
                  </code>
                ),
                description: 'The length of the video in seconds.',
              },
              {
                property: (
                  <code>
                    <b>percent</b>
                  </code>
                ),
                description:
                  'The amount of the video that has played from the current playback position in comparison to the length of the video; multiply by 100 to obtain the percentage.',
              },
              {
                property: (
                  <code>
                    <b>seconds</b>
                  </code>
                ),
                description:
                  'The amount of the video, in seconds, that has played from the current playback position.',
              },
            ],
            sample: {
              duration: 61.857,
              percent: 0.049,
              seconds: 3.034,
            },
          },
        },
        {
          name: 'volumechange',
          description: (
            <Paragraph size="2">This event fires when the volume in the player changes.</Paragraph>
          ),
          note: "Some devices don't support the ability to set the volume of the video independently of the system volume, so this event never fires on those devices.",
          event: {
            data: [
              {
                property: (
                  <code>
                    <b>volume</b>
                  </code>
                ),
                description: 'The new volume level.',
              },
              {
                property: (
                  <code>
                    <b>muted</b>
                  </code>
                ),
                description: 'The muted state of the player.',
              },
            ],
            sample: {
              volume: 0.5,
              muted: false,
            },
          },
        },
      ],
    },
    'Methods for chapters': {
      type: 'method',
      items: [
        {
          name: 'Get the chapters of a video',
          description: (
            <div>
              <Paragraph size="2">
                This method gets the chapters of the video as an array. Each chapter object looks
                like this:
              </Paragraph>
              <CodeBlock language="json">
                {{
                  startTime: 15,
                  title: 'Chapter Title',
                  index: 1,
                }}
              </CodeBlock>
            </div>
          ),
          function: {
            name: 'getChapters()',
            returns: 'Promise <array, Error>',
            errors: [
              {
                error: 'Error',
                description: 'An unexpected error occurred.',
              },
            ],
          },
          example: (
            <CodeBlock language="javascript">
              {`player.getChapters().then(function(chapters) {
  // \`chapters\` indicates an array of chapter objects
}).catch(function(error) {
  // An error occurred
});`}
            </CodeBlock>
          ),
        },
        {
          name: 'Get the current chapter of a video',
          description: (
            <Paragraph size="2">
              This method gets the current chapter of a video based on the playback position of the
              video and the start time of the chapter. For a chapter to be current, the playback
              position must be greater than or equal to the chapter's start time but less than the
              start time of the subsequent chapter.
            </Paragraph>
          ),
          function: {
            name: 'getCurrentChapter()',
            returns: 'Promise <object, Error>',
            errors: [
              {
                error: 'Error',
                description: 'An unexpected error occurred.',
              },
            ],
          },
          example: (
            <CodeBlock language="javascript">
              {`player.getCurrentChapter().then(function(chapter) {
  // \`chapter\` refers to a chapter object
}).catch(function(error) {
  // An error occurred
});`}
            </CodeBlock>
          ),
        },
      ],
    },
    'Events for chapters': {
      type: 'event',
      items: [
        {
          name: 'chapterchange',
          description: (
            <Paragraph size="2">This event fires when the current chapter changes.</Paragraph>
          ),
          event: {
            data: [
              {
                property: (
                  <code>
                    <b>index</b>
                  </code>
                ),
                description: 'The chapter number.',
              },
              {
                property: (
                  <code>
                    <b>startTime</b>
                  </code>
                ),
                description: 'The time in seconds when the chapter begins.',
              },
              {
                property: (
                  <code>
                    <b>title</b>
                  </code>
                ),
                description: 'The chapter title.',
              },
            ],
            sample: {
              index: 1,
              startTime: 15,
              title: 'Chapter 1',
            },
          },
          example: (
            <CodeBlock language="javascript">
              {`var onPlay = function(data) {
  console.log(data.index);
  console.log(data.startTime);
  console.log(data.title);
};
player.on('chapterchange', onPlay);`}
            </CodeBlock>
          ),
        },
      ],
    },
    'Methods for cue points': {
      type: 'method',
      items: [
        {
          name: 'Add a cue point to a video',
          description: (
            <Paragraph size="2">
              This method adds a cue point to the video. Cue points fire a <code>cuepoint</code>{' '}
              event when the playback position of the video passes the specified time.
            </Paragraph>
          ),
          note: 'Cue points should be accurate to within a tenth of a second, but the precision may vary based on the browser or environment.',
          function: {
            name: 'addCuePoint(time, data)',
            parameters: [
              {
                parameter: (
                  <code>
                    <b>data</b>
                  </code>
                ),
                type: 'object',
                required: true,
                description: 'An object associated with the cue point event.',
              },
              {
                parameter: (
                  <code>
                    <b>time</b>
                  </code>
                ),
                type: 'number',
                required: true,
                description: 'The location of the cue point in seconds.',
              },
            ],
            returns: 'Promise <string, (UnsupportedError | RangeError | Error)>',
            errors: [
              {
                error: 'UnsupportedError',
                description: "Cue points aren't supported by the current player or browser.",
              },
              {
                error: 'RangeError',
                description: "The time is less than 0 or greater than the video's duration.",
              },
              {
                error: 'Error',
                description: 'Some other error occurred.',
              },
            ],
          },
          example: (
            <CodeBlock language="javascript">{`player.addCuePoint(15, {
  customKey: 'customValue'
}).then(function(id) {
  // The cue point is added
}).catch(function(error) {
  switch (error.name) {
      case 'UnsupportedError':
          // Cue points aren't supported by the current player or browser
          break;
      case 'RangeError':
          // The time is less than 0 or greater than the video's duration
          break;
      default:
          // Some other error occurred
          break;
  }
});`}</CodeBlock>
          ),
        },
        {
          name: 'Get the cue points of a video',
          description: (
            <div>
              <Paragraph size="2">
                This method gets the cue points of the video as an array. Each cue point object
                looks like this:
              </Paragraph>

              <CodeBlock language="json">
                {{
                  time: '{time}',
                  data: {
                    '{customKey}': '{customValue}',
                  },
                  id: '{id}',
                }}
              </CodeBlock>
            </div>
          ),
          function: {
            name: 'getCuePoints()',
            returns: 'Promise <array, (UnsupportedError | Error)>',
            errors: [
              {
                error: 'UnsupportedError',
                description: "Cue points aren't supported by the current player or browser.",
              },
              {
                error: 'Error',
                description: 'Some other error occurred.',
              },
            ],
          },
          example: (
            <CodeBlock language="javascript">{`player.getCuePoints().then(function(cuePoints) {
  // \`cuePoints\` indicates an array of cue point objects
}).catch(function(error) {
  switch (error.name) {
      case 'UnsupportedError':
          // Cue points aren't supported by the current player or browser
          break;
      default:
          // Some other error occurred
          break;
  }
});`}</CodeBlock>
          ),
        },
        {
          name: 'Remove a cue point from a video',
          description: (
            <Paragraph size="2">
              This method removes the specified cue point from the video.
            </Paragraph>
          ),
          function: {
            name: 'removeCuePoint(id)',
            parameters: [
              {
                parameter: (
                  <code>
                    <b>id</b>
                  </code>
                ),
                type: 'string',
                required: true,
                description: (
                  <span>
                    The ID of the cue point as returned by the <code>getCuePoints()</code> method.
                  </span>
                ),
              },
            ],
            returns: 'Promise <string, (UnsupportedError | InvalidCuePoint | Error)>',
            errors: [
              {
                error: 'UnsupportedError',
                description: "Cue points aren't supported by the current player or browser.",
              },
              {
                error: 'InvalidCuePoint',
                description: 'There is no cue point with the specified ID.',
              },
              {
                error: 'Error',
                description: 'Some other error occurred.',
              },
            ],
          },
          example: (
            <CodeBlock language="javascript">{`player.removeCuePoint('09ecf4e4-b587-42cf-ad9f-e666b679c9ab').then(function(id) {
  // The cue point is removed
}).catch(function(error) {
  switch (error.name) {
      case 'UnsupportedError':
          // Cue points aren't supported by the current player or browser
          break;
      case 'InvalidCuePoint':
          // There is no cue point with the specified ID
          break;
      default:
          // Some other error occurred
          break;
  }
});`}</CodeBlock>
          ),
        },
      ],
    },
    'Events for cue points': {
      type: 'event',
      items: [
        {
          name: 'cuechange',
          description: (
            <div>
              <Paragraph size="2">This event fires under two conditions:</Paragraph>

              <ul>
                <li>When the active cue for the current text track changes</li>
                <li>When the active text track changes</li>
              </ul>

              <Paragraph size="2">Multiple cues may be active at a time.</Paragraph>
            </div>
          ),
          event: {
            data: [
              {
                property: (
                  <code>
                    <b>cues</b>
                  </code>
                ),
                description: 'The set of values representing the text of the text track.',
              },
              {
                property: (
                  <code>
                    <b>cues.html</b>
                  </code>
                ),
                description: 'The HTML of the text track that the player renders for the cue.',
              },
              {
                property: (
                  <code>
                    <b>cues.text</b>
                  </code>
                ),
                description:
                  'The raw value of the text track parsed from the caption or subtitle file.',
              },
              {
                property: (
                  <code>
                    <b>kind</b>
                  </code>
                ),
                description: (
                  <span>
                    The kind of the text track: <code>captions</code> or <code>subtitles</code>.
                  </span>
                ),
              },
              {
                property: (
                  <code>
                    <b>label</b>
                  </code>
                ),
                description:
                  'The human-readable label of the text track for identification purposes.',
              },
              {
                property: (
                  <code>
                    <b>language</b>
                  </code>
                ),
                description: "The ISO code of the text track's language.",
              },
            ],
            sample: {
              cues: [
                {
                  html: "<i>Here at Vimeo, there's always <br />one thing on our minds:</i>",
                  text: "<i>Here at Vimeo, there's always one thing on our minds:</i>",
                },
              ],
              kind: 'captions',
              label: 'English CC',
              language: 'en',
            },
          },
        },
        {
          name: 'cuepoint',
          description: 'This event fires when the playback position hits a registered cue point.',
          event: {
            data: [
              {
                property: (
                  <code>
                    <b>data</b>
                  </code>
                ),
                description: (
                  <span>
                    The custom data from the <code>addCuePoint()</code> call, or an empty object.
                  </span>
                ),
              },
              {
                property: (
                  <code>
                    <b>id</b>
                  </code>
                ),
                description: 'The ID of the cue point.',
              },
              {
                property: (
                  <code>
                    <b>time</b>
                  </code>
                ),
                description: 'The location of the cue point in seconds.',
              },
            ],
            sample: {
              time: 15,
              data: {
                customKey: 'customValue',
              },
              id: '40f5722b-09aa-4060-a887-3c81aaa37cce',
            },
          },
        },
      ],
    },
    'Methods for text tracks': {
      type: 'method',
      items: [
        {
          name: 'Disable the text track overlay in a player',
          description: (
            <Paragraph size="2">
              This method disables the text track overlay in the player, which hides the text track.
            </Paragraph>
          ),
          function: {
            name: 'disableTextTrack()',
            returns: 'Promise <void>',
          },
          example: (
            <CodeBlock language="javascript">{`player.disableTextTrack().then(function() {
  // The text track is disabled
});`}</CodeBlock>
          ),
        },
        {
          name: 'Enable a text track in a video',
          description: (
            <Paragraph size="2">
              This method enables the text track with the specified language and, optionally, the
              specified kind of either captions or subtitles.
            </Paragraph>
          ),
          function: {
            name: 'enableTextTrack(language, kind)',
            parameters: [
              {
                parameter: (
                  <code>
                    <b>kind</b>
                  </code>
                ),
                type: 'string',
                required: false,
                description: (
                  <span>
                    The kind of the text track: <code>captions</code> or <code>subtitles</code>.
                  </span>
                ),
              },
              {
                parameter: (
                  <code>
                    <b>language</b>
                  </code>
                ),
                type: 'string',
                required: true,
                description: "The ISO code of the text track's language.",
              },
            ],
            returns: 'Promise <object, (InvalidTrackLanguageError | InvalidTrackError | Error)>',
            errors: [
              {
                error: 'InvalidTrackLanguageError',
                description: 'There is no text track for the specified language.',
              },
              {
                error: 'InvalidTrackError',
                description: 'There is no such text track.',
              },
              {
                error: 'Error',
                description: 'Some other error occurred.',
              },
            ],
          },
          example: (
            <CodeBlock language="javascript">{`player.enableTextTrack('en').then(function(track) {
  // \`track.language\` indicates the ISO code of the text track language
  // \`track.kind\` indicates the kind of the text track
  // \`track.label\` indicates the the human-readable label of the text track
}).catch(function(error) {
  switch (error.name) {
      case 'InvalidTrackLanguageError':
          // There is no text track for the specified language
          break;
      case 'InvalidTrackError':
          // There is no such text track
          break;
      default:
          // Some other error occurred
          break;
  }
});`}</CodeBlock>
          ),
        },
        {
          name: 'Get the text tracks of a video',
          description: (
            <div>
              <Paragraph size="2">
                This method gets the text tracks of the video as an array. Each text track object
                looks like this:
              </Paragraph>

              <CodeBlock language="json">
                {{
                  label: '{label}',
                  language: '{language_code}',
                  kind: '{kind}',
                  mode: '{mode}',
                }}
              </CodeBlock>

              <Paragraph size="2">
                The kind can be either <code>captions</code> or <code>subtitles</code>, and the mode
                can be either <code>showing</code> or <code>disabled</code>. Only one track can be
                showing at any given time; the rest are disabled.
              </Paragraph>
            </div>
          ),
          function: {
            name: 'getTextTracks()',
            returns: 'Promise <array>',
          },
          example: (
            <CodeBlock language="javascript">{`player.getTextTracks().then(function(tracks) {
  // \`tracks\` indicates an array of text track objects
});`}</CodeBlock>
          ),
        },
      ],
    },
    'Events for text tracks': {
      type: 'event',
      items: [
        {
          name: 'texttrackchange',
          description: (
            <Paragraph size="2">
              This event fires when the active text track of the <code>captions</code> or{' '}
              <code>subtitles</code> kind changes.
            </Paragraph>
          ),
          note: (
            <span>
              If text tracks are turned off, all the values of the event data come back as{' '}
              <code>null</code>.
            </span>
          ),
          event: {
            data: [
              {
                property: (
                  <code>
                    <b>kind</b>
                  </code>
                ),
                description: (
                  <span>
                    The kind of the text track: <code>captions</code> or <code>subtitles</code>.
                  </span>
                ),
              },
              {
                property: (
                  <code>
                    <b>label</b>
                  </code>
                ),
                description:
                  'The human-readable label of the text track for identification purposes.',
              },
              {
                property: (
                  <code>
                    <b>language</b>
                  </code>
                ),
                description: "The ISO code of the text track's language.",
              },
            ],
            sample: {
              kind: 'captions',
              label: 'English CC',
              language: 'en',
            },
          },
        },
      ],
    },
    'Methods for player details': {
      type: 'method',
      items: [
        {
          name: 'Destroy a player',
          description: (
            <Paragraph size="2">
              This method cleans up the player and removes it from the Document Object Model. After
              you execute this method, you can't use the affected player.
            </Paragraph>
          ),
          function: {
            name: 'destroy()',
            returns: 'Promise <void>',
          },
          example: (
            <CodeBlock language="javascript">{`player.destroy().then(function() {
  // The player is destroyed
});`}</CodeBlock>
          ),
        },
        {
          name: 'Get the accent color of a player',
          description: (
            <Paragraph size="2">
              This method gets the hexadecimal accent color value of the player. For more
              information about player colors, see{' '}
              <Link variant="minimal" href="/player/sdk/reference#get-the-colors-of-a-player">
                Get the colors of a player
              </Link>
              .
            </Paragraph>
          ),
          function: {
            name: 'getColor()',
            returns: 'Promise <string>',
          },
          example: (
            <CodeBlock language="javascript">{`player.getColor().then(function(color) {
  // \`color\` indicates the hex accent color of the player
});`}</CodeBlock>
          ),
        },
        {
          name: 'Get the colors of a player',
          description: (
            <Paragraph size="2">
              This method gets the hexadecimal color values of the player. The order in which the
              array of color values is set is <code>[Primary, Accent, Text/Icon, Background]</code>.
            </Paragraph>
          ),
          function: {
            name: 'getColors()',
            returns: 'Promise <array>',
          },
          example: (
            <CodeBlock language="javascript">{`player.getColors().then(function(colors) {
  // \`colors\` indicates the hex colors of the player
});`}</CodeBlock>
          ),
        },
        {
          name: 'Get the embed code of a video',
          description: (
            <Paragraph size="2">
              This method gets the embed code of the video as an <code>{`<iframe>`}</code> HTML
              element.
            </Paragraph>
          ),
          function: {
            name: 'getVideoEmbedCode()',
            returns: 'Promise <string>',
          },
          example: (
            <CodeBlock language="javascript">{`player.getVideoEmbedCode().then(function(embedCode) {
  // \`embedCode\` indicates the <iframe> embed code
});`}</CodeBlock>
          ),
        },
        {
          name: 'Get the height of a video',
          description: (
            <div>
              <Paragraph size="2">
                This method gets the native height of the video as the height of the video's highest
                available resolution.
              </Paragraph>

              <Paragraph size="2">To get both the width and height, you can do this:</Paragraph>

              <CodeBlock language="javascript">{`Promise.all([player.getVideoWidth(), player.getVideoHeight()]).then(function(dimensions) {
  var width = dimensions[0];
  var height = dimensions[1];
});`}</CodeBlock>
            </div>
          ),
          function: {
            name: 'getVideoHeight()',
            returns: 'Promise <number>',
          },
          example: (
            <CodeBlock language="javascript">{`player.getVideoHeight().then(function(height) {
  // height = The height of the video that is currently playing
});`}</CodeBlock>
          ),
        },
        {
          name: 'Get the ID of a video',
          description: <Paragraph size="2">This method gets the ID code of the video.</Paragraph>,
          function: {
            name: 'getVideoId()',
            returns: 'Promise <number>',
          },
          example: (
            <CodeBlock language="javascript">{`player.getVideoId().then(function(id) {
  // \`id\` indicates the ID of the video
});`}</CodeBlock>
          ),
        },
        {
          name: 'Get the title of a video',
          description: <Paragraph size="2">This method gets the title of the video.</Paragraph>,
          function: {
            name: 'getVideoTitle()',
            returns: 'Promise <string>',
          },
          example: (
            <CodeBlock language="javascript">{`player.getVideoTitle().then(function(title) {
  // title = The title of the video
});`}</CodeBlock>
          ),
        },
        {
          name: 'Get the URL of a video',
          description: (
            <Paragraph size="2">This method gets the URL of the video on vimeo.com.</Paragraph>
          ),
          function: {
            name: 'getVideoUrl()',
            returns: 'Promise <string, (PrivacyError | Error)>',
            errors: [
              {
                error: 'PrivacyError',
                description: "The URL isn't available because of the video's privacy setting.",
              },
              {
                error: 'Error',
                description: 'Some other error occurred.',
              },
            ],
          },
          example: (
            <CodeBlock language="javascript">{`player.getVideoUrl().then(function(url) {
  // \`url\` indicates the vimeo.com URL of the video
}).catch(function(error) {
  switch (error.name) {
      case 'PrivacyError':
          // The URL isn't available because of the video's privacy setting
          break;
      default:
          // Some other error occurred
          break;
  }
});`}</CodeBlock>
          ),
        },
        {
          name: 'Get the width of a video',
          description: (
            <div>
              <Paragraph size="2">
                This method gets the native width of the video as the width of the video's highest
                available resolution.
              </Paragraph>

              <Paragraph size="2">To get both the width and height, you can do this:</Paragraph>

              <CodeBlock language="javascript">{`Promise.all([player.getVideoWidth(), player.getVideoHeight()]).then(function(dimensions) {
  var width = dimensions[0];
  var height = dimensions[1];
});`}</CodeBlock>
            </div>
          ),
          function: {
            name: 'getVideoWidth()',
            returns: 'Promise <number>',
          },
          example: (
            <CodeBlock language="javascript">{`player.getVideoWidth().then(function(width) {
  // width = The width of the video that is currently playing
}).catch(function(error) {
  // An error occurred
});`}</CodeBlock>
          ),
        },
        {
          name: 'Load a new video into a player',
          description: (
            <Paragraph size="2">
              This method loads a new video into an existing player. If the video loads
              successfully, the promise is resolved. If the video fails to load, the promise is
              rejected.
            </Paragraph>
          ),
          function: {
            name: 'loadVideo(id | url | object)',
            parameters: [
              {
                parameter: (
                  <span>
                    <code>
                      <b>id</b>
                    </code>
                    {', '}
                    <code>
                      <b>url</b>
                    </code>{' '}
                    <em>or</em>{' '}
                    <code>
                      <b>object</b>
                    </code>
                  </span>
                ),
                type: (
                  <span>
                    Number, String, <em>or</em> Object
                  </span>
                ),
                required: true,
                description: (
                  <span>
                    The ID or the URL of the video to load, which can also be provided within an
                    object of embed options. If the video's{' '}
                    <Link
                      variant="minimal"
                      href="https://vimeo.com/help/sso?redirect_to=https://help.vimeo.com/hc/en-us/articles/12426199699985-Overview-of-video-privacy-settings"
                    >
                      privacy setting
                    </Link>{' '}
                    is <code>Unlisted</code>, you must provide a URL, and the URL must include the{' '}
                    <code>
                      <b>h</b>
                    </code>{' '}
                    parameter. See our{' '}
                    <Link variant="minimal" href="/player/sdk/basics#connecting-to-a-vimeo-player">
                      introductory guide
                    </Link>{' '}
                    for more information.
                  </span>
                ),
              },
            ],
            returns: 'Promise <number, (TypeError | PasswordError | PrivacyError | Error)>',
            errors: [
              {
                error: 'TypeError',
                description: "The ID isn't a number.",
              },
              {
                error: 'PasswordError',
                description: 'The video is password-protected.',
              },
              {
                error: 'PrivacyError',
                description: 'The video is private.',
              },
              {
                error: 'Error',
                description: 'Some other error occurred.',
              },
            ],
          },
          example: (
            <CodeBlock language="javascript">{`player.loadVideo(76979871).then(function(id) {
  // The new video is loaded
}).catch(function(error) {
  switch (error.name) {
      case 'TypeError':
          // The ID isn't a number
          break;
      case 'PasswordError':
          // The video is password-protected
          break;
      case 'PrivacyError':
          // The video is private
          break;
      default:
          // Some other error occurred
          break;
  }
});`}</CodeBlock>
          ),
        },
        {
          name: 'Remove an event listener from a player',
          description: (
            <Paragraph size="2">
              This method removes the listener for a given event and, optionally, the specified
              callback. If you don't indicate a callback, the method removes all listeners for the
              event.
            </Paragraph>
          ),
          function: {
            name: 'off(event, callback)',
            parameters: [
              {
                parameter: (
                  <code>
                    <b>callback</b>
                  </code>
                ),
                type: 'function',
                required: false,
                description: 'The callback of the listener to remove.',
              },
              {
                parameter: (
                  <code>
                    <b>event</b>
                  </code>
                ),
                type: 'string',
                required: true,
                description: 'The event whose listener you want to remove.',
              },
            ],
          },
          example: (
            <CodeBlock language="javascript">{`var onPlay = function(data) {
  // \`data\` is an object containing properties specific to that event
};
player.on('play', onPlay);
// If later you decide that you don't need to listen for \`play\`
player.off('play', onPlay);
// Alternatively, \`off\` can be called with just the event name to remove all
// listeners
player.off('play');`}</CodeBlock>
          ),
        },
        {
          name: 'Add an event listener to a player',
          description: 'This method adds an event listener for the specified event.',
          function: {
            name: 'on(event, callback)',
            parameters: [
              {
                parameter: (
                  <code>
                    <b>callback</b>
                  </code>
                ),
                type: 'function',
                required: true,
                description: 'The callback of the listener to add.',
              },
              {
                parameter: (
                  <code>
                    <b>event</b>
                  </code>
                ),
                type: 'string',
                required: true,
                description: 'The event of the listener to add.',
              },
            ],
          },
          example: (
            <CodeBlock language="javascript">{`var onPlay = function(data) {
  // \`data\` is an object containing properties specific to that event
};
player.on('play', onPlay);`}</CodeBlock>
          ),
        },
        {
          name: 'Execute a function once the player is ready ',
          description: (
            <Paragraph size="2">
              This method triggers a function as soon as the player's <code>{`<iframe>`}</code>{' '}
              element initializes. You don't need to wait for this method to trigger to begin adding
              event listeners or calling other methods.
            </Paragraph>
          ),
          function: {
            name: 'ready()',
            returns: 'Promise <void>',
          },
          example: (
            <CodeBlock language="javascript">{`player.ready().then(function() {
  // The player is ready
});`}</CodeBlock>
          ),
        },
        {
          name: 'Set the accent color of a player',
          description: (
            <Paragraph size="2">
              This method sets the accent color of the player to a hexadecimal or RGB color value.
              Setting the color might fail if the owner of the video has set their embed preferences
              to force a specific color. For more information about player colors, see{' '}
              <Link variant="minimal" href="/player/sdk/reference#set-the-colors-of-a-player">
                Set the colors of a player
              </Link>
              .
            </Paragraph>
          ),
          function: {
            name: 'setColor(color)',
            parameters: [
              {
                parameter: (
                  <code>
                    <b>color</b>
                  </code>
                ),
                type: 'string',
                required: true,
                description: 'The hexadecimal or RGB accent color value to set.',
              },
            ],
            returns: 'Promise <string, (TypeError | EmbedSettingsError | Error)>',
            errors: [
              {
                error: 'TypeError',
                description: "The string isn't a valid hexadecimal or RGB color value.",
              },
              {
                error: 'EmbedSettingsError',
                description: 'The owner of the video has chosen to use a specific color.',
              },
              {
                error: 'Error',
                description: 'Some other error occurred.',
              },
            ],
          },
          example: (
            <CodeBlock language="javascript">{`player.setColor('#00adef').then(function(color) {
  // The color was set
}).catch(function(error) {
  switch (error.name) {
      case 'TypeError':
          // The string isn't a valid hexadecimal or RGB value
          break;
      case 'EmbedSettingsError':
          // The owner of the video has chosen to use a specific color
          break;
      default:
          // Some other error occurred
          break;
  }
});`}</CodeBlock>
          ),
        },
        {
          name: 'Set the colors of a player',
          description: (
            <Paragraph size="2">
              This method sets the colors of the player to either the hexadecimal or RGB color
              values. Setting the colors might fail if the owner of the video has set their embed
              preferences to force a specific color. The order in which the array of color values is
              set is <code>[Primary, Accent, Text/Icon, Background]</code>. Empty or null values use
              the default color for that index.
            </Paragraph>
          ),
          function: {
            name: 'setColors(colors)',
            parameters: [
              {
                parameter: (
                  <code>
                    <b>colors</b>
                  </code>
                ),
                type: 'Array',
                required: true,
                description: (
                  <span>
                    An array of the hexadecimal or RGB color values to set. The order of the color
                    values is <code>[Primary, Accent, Text/Icon, Background]</code>.
                  </span>
                ),
              },
            ],
            returns: 'Promise <array, (TypeError | EmbedSettingsError | Error)>',
            errors: [
              {
                error: 'TypeError',
                description: "The string isn't a valid hexadecimal or RGB color value.",
              },
              {
                error: 'EmbedSettingsError',
                description: 'The owner of the video has chosen to use a specific color.',
              },
              {
                error: 'Error',
                description: 'Some other error occurred.',
              },
            ],
          },
          example: (
            <CodeBlock language="javascript">{`player.setColors(['#000000', '#00adef', '#ffffff', '#000000']).then(function(colors) {
  // The colors were set
}).catch(function(error) {
  switch (error.name) {
      case 'TypeError':
          // The string isn't a valid hexadecimal or RGB value
          break;
      case 'EmbedSettingsError':
          // The owner of the video has chosen to use a specific color
          break;
      default:
          // Some other error occurred
          break;
  }
});`}</CodeBlock>
          ),
        },
        {
          name: 'Unload the video from a player',
          description: (
            <Paragraph size="2">
              This method unloads the video from the player. The player returns to its initial
              state.
            </Paragraph>
          ),
          function: {
            name: 'unload()',
            returns: 'Promise <void>',
          },
          example: (
            <CodeBlock language="javascript">{`player.unload().then(function() {
  // The video has been unloaded
});`}</CodeBlock>
          ),
        },
      ],
    },
    'Events for interactive videos': {
      type: 'event',
      items: [
        {
          name: 'interactivehotspotclicked',
          description: <Paragraph size="2">This event fires when a hotspot is clicked.</Paragraph>,
          event: {
            data: [
              {
                property: (
                  <code>
                    <b>action</b>
                  </code>
                ),
                description: (
                  <span>
                    The action type of the hotspot. Possible values include <code>overlay</code>,{' '}
                    <code>url</code>, <code>seek</code>, <code>event</code>, and <code>none</code>.
                  </span>
                ),
              },
              {
                property: (
                  <code>
                    <b>actionPreference</b>
                  </code>
                ),
                description: <span>The user's preferred action type for the hotspot.</span>,
              },
              {
                property: (
                  <code>
                    <b>actionPreference.pauseOnAction</b>
                  </code>
                ),
                description: (
                  <span>
                    Whether to pause when the action type is <code>overlay</code>, <code>url</code>,{' '}
                    <code>seek</code>, <code>event</code>, or <code>none</code>.
                  </span>
                ),
              },
              {
                property: (
                  <code>
                    <b>actionPreference.overlayId</b>
                  </code>
                ),
                description: (
                  <span>
                    When{' '}
                    <code>
                      <b>action</b>
                    </code>{' '}
                    is <code>overlay</code>, the displayed unique ID for the overlay when the
                    hotspot is clicked.
                  </span>
                ),
              },
              {
                property: (
                  <code>
                    <b>actionPreference.seekTo</b>
                  </code>
                ),
                description: (
                  <span>
                    When{' '}
                    <code>
                      <b>action</b>
                    </code>{' '}
                    is <code>seek</code>, the time in seconds that the video should jump to when the
                    hotspot is clicked.
                  </span>
                ),
              },
              {
                property: (
                  <code>
                    <b>actionPreference.url</b>
                  </code>
                ),
                description: 'The target URL when the hotspot is clicked.',
              },
              {
                property: (
                  <code>
                    <b>currentTime</b>
                  </code>
                ),
                description: <span>The current video's progress in seconds.</span>,
              },
              {
                property: (
                  <code>
                    <b>customPayloadData</b>
                  </code>
                ),
                description: 'The custom payload configured in Studio for the hotspot.',
              },
              {
                property: (
                  <code>
                    <b>hotspotId</b>
                  </code>
                ),
                description: 'The unique ID for the hotspot.',
              },
            ],
            sample: {
              action: 'seek',
              actionPreference: {
                pauseOnAction: false,
                overlayId: 864334,
                seekTo: 30,
                url: '<https://your-url.com>',
              },
              currentTime: 15.585,
              customPayloadData: null,
              hotspotId: 8148223,
            },
          },
        },
        {
          name: 'interactiveoverlaypanelclicked',
          description: (
            <Paragraph size="2">
              This event fires when the buttons or images within the interactive overlay panel are
              clicked.
            </Paragraph>
          ),
          event: {
            data: [
              {
                property: (
                  <code>
                    <b>action</b>
                  </code>
                ),
                description: (
                  <span>
                    The button or image panel click action. Possible values include{' '}
                    <code>clickthrough</code>, <code>seek</code>, <code>close</code>,{' '}
                    <code>event</code>, and <code>none</code>.
                  </span>
                ),
              },
              {
                property: (
                  <code>
                    <b>actionPreference</b>
                  </code>
                ),
                description: <span>The user's preferred action type for the overlay panel.</span>,
              },
              {
                property: (
                  <code>
                    <b>actionPreference.pauseOnAction</b>
                  </code>
                ),
                description: (
                  <span>
                    Whether to pause when the action type is <code>clickthrough</code>,{' '}
                    <code>seek</code>, <code>close</code>, <code>event</code>, or <code>none</code>.
                  </span>
                ),
              },
              {
                property: (
                  <code>
                    <b>actionPreference.seekTo</b>
                  </code>
                ),
                description: (
                  <span>
                    When{' '}
                    <code>
                      <b>action</b>
                    </code>{' '}
                    is <code>seek</code>, the time in seconds that the video should jump to when the
                    overlay panel is clicked.
                  </span>
                ),
              },
              {
                property: (
                  <code>
                    <b>actionPreference.url</b>
                  </code>
                ),
                description: (
                  <span>
                    When{' '}
                    <code>
                      <b>action</b>
                    </code>{' '}
                    is <code>clickthrough</code>, the target URL when the overlay panel is clicked.
                  </span>
                ),
              },
              {
                property: (
                  <code>
                    <b>currentTime</b>
                  </code>
                ),
                description: <span>The current video's progress in seconds.</span>,
              },
              {
                property: (
                  <code>
                    <b>customPayloadData</b>
                  </code>
                ),
                description: 'The custom payload configured in Studio for the overlay panel.',
              },
              {
                property: (
                  <code>
                    <b>hotspotId</b>
                  </code>
                ),
                description: 'The unique ID for the overlay.',
              },
              {
                property: (
                  <code>
                    <b>panelId</b>
                  </code>
                ),
                description: 'The unique ID for a panel within the overlay.',
              },
            ],
            sample: {
              action: 'seek',
              actionPreference: {
                pauseOnAction: true,
                seekTo: 30,
                url: '<https://your-url.com>',
              },
              currentTime: 25.67,
              customPayloadData: null,
              overlayId: 864334,
              panelId: 'c47193a0-8320-4572-9bcd-8425851b36e9',
            },
          },
        },
      ],
    },
  };
}
